import React from 'react';
import { useTranslation } from 'react-i18next';
import Immutable from 'immutable';
import { FormTextField } from '@uncinc/uncinc-react-kitchen-sink';

import AddressFields from '../../../../../components/AddressFields';
import AddressTeaser from '../../../../../components/AddressOverview/AddressTeaser';
import BillingAddressSelector from '../BillingAddressSelector';
import type { CheckoutFormValues } from '../../types/checkout.types';

import './style.scss';

/**
 * Billing Address Section Component
 *
 * Renders the billing (factuur) block per design 333:3084 / 333:2789. The
 * order's reference + invoice e-mail are always-shown order fields at the top
 * (they are sent with the order, not part of the selected address). Below them
 * the billing address shows as a read-only card with a "Wijzigen" action that
 * opens the address-book chooser when an address is selected (a default billing
 * address exists, or the user picked one); with no selection the user can pick
 * one from the address book or enter it inline (mirroring the delivery block).
 * The organisation field shows only for a business order.
 *
 * @component
 */

interface BillingAddressSectionProps {
  /** Form values containing billing address data */
  values: CheckoutFormValues;
  /** React ref to the checkout form (omitted in contexts without the selector) */
  // TODO: Replace 'any' with proper Formik ref type when available
  formRef?: React.RefObject<any>;
  /** Shared populator; called with the 'billing_' prefix */
  // TODO: Replace 'any' with proper address type definition
  selectAddress?: (
    ref: React.RefObject<any>,
    address: any,
    prefix?: string
  ) => void;
  /** Currently selected billing address index, or null if none selected */
  selectedBillingAddressIndex?: number | null;
  /** Callback to update the selected billing address index */
  setSelectedBillingAddressIndex?: (index: number) => void;
  /** The user's saved addresses (Immutable.List in v1, plain array in v2) */
  // TODO: Replace 'any' with proper address type definition
  addressesRaw?: Immutable.List<any> | any[];
  /** Index of the user's default billing address (drives the badge) */
  defaultBillingIndex?: number | null;
}

const BillingAddressSection: React.FC<BillingAddressSectionProps> = ({
  values,
  formRef,
  selectAddress = () => {},
  selectedBillingAddressIndex = null,
  setSelectedBillingAddressIndex = () => {},
  addressesRaw,
  defaultBillingIndex = null,
}) => {
  const { t } = useTranslation();
  const billingSelected =
    selectedBillingAddressIndex !== null &&
    selectedBillingAddressIndex !== undefined;

  // addressesRaw is an Immutable.List in checkout v1 and a plain array in v2;
  // resolve the selected entry to an Immutable map for the teaser.
  const resolveSelectedAddress = () => {
    if (!billingSelected || !addressesRaw) return null;
    const list = addressesRaw as any;
    const raw = list.get
      ? list.get(selectedBillingAddressIndex)
      : list[selectedBillingAddressIndex];
    if (!raw) return null;
    return Immutable.Map.isMap(raw) ? raw : Immutable.fromJS(raw);
  };
  const selectedAddress = resolveSelectedAddress();
  const isDefaultBilling =
    billingSelected &&
    defaultBillingIndex !== null &&
    Number(selectedBillingAddressIndex) === Number(defaultBillingIndex);

  return (
    <fieldset>
      <legend>{t('checkout.billing_info')}</legend>
      <div className="CheckoutAddressBlock">
        <FormTextField
          title={t('checkout.customer_reference.label')}
          fieldName="customer_reference"
        />
        <FormTextField
          title={t('checkout.invoice_email.label')}
          fieldName="invoice_email"
        />
        {billingSelected && selectedAddress ? (
          <div className="CheckoutAddressBlock__selected">
            <AddressTeaser
              address={selectedAddress}
              isDefaultBilling={isDefaultBilling}
            />
            {Boolean(formRef) && (
              <BillingAddressSelector
                formRef={formRef!}
                selectAddress={selectAddress}
                selectedBillingAddressIndex={selectedBillingAddressIndex}
                setSelectedBillingAddressIndex={setSelectedBillingAddressIndex}
              />
            )}
          </div>
        ) : (
          <>
            {Boolean(formRef) && (
              <BillingAddressSelector
                formRef={formRef!}
                selectAddress={selectAddress}
                selectedBillingAddressIndex={selectedBillingAddressIndex}
                setSelectedBillingAddressIndex={setSelectedBillingAddressIndex}
              />
            )}
            <AddressFields
              prefix="billing_"
              showOrganisation={values.business_option === 'business'}
              organisationRequired
            />
          </>
        )}
      </div>
    </fieldset>
  );
};

export default BillingAddressSection;
